import { apiClient as api } from './api';
import { callV3, type ApiResponse } from '../api/envelope';
import type {
  CreateSessionRequest,
  HistoryListResponse,
  RevertToHistoryRequest,
  SessionDetailsResponse,
  SessionsListResponse,
  UndoRedoResponse,
  UpdateSessionRequest,
  UserAssetsListResponse,
} from '../domain';

const BASE = '/api/v3/operations/sessions';

export interface ListSessionsParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const SessionService = {
  create: (request: CreateSessionRequest) =>
    callV3<SessionDetailsResponse>(
      api.post<ApiResponse<SessionDetailsResponse>>(BASE, request),
    ),

  list: ({ page = 1, pageSize = 20, sortBy = 'updatedAt', sortOrder = 'desc' }: ListSessionsParams = {}) =>
    callV3<SessionsListResponse>(
      api.get<ApiResponse<SessionsListResponse>>(BASE, {
        params: { page, pageSize, sortBy, sortOrder },
      }),
    ),

  get: (sessionId: string) =>
    callV3<SessionDetailsResponse>(
      api.get<ApiResponse<SessionDetailsResponse>>(`${BASE}/${sessionId}`),
    ),

  update: (sessionId: string, request: UpdateSessionRequest) =>
    callV3<SessionDetailsResponse>(
      api.patch<ApiResponse<SessionDetailsResponse>>(`${BASE}/${sessionId}`, request),
    ),

  remove: async (sessionId: string, deleteImages = false): Promise<void> => {
    await callV3<unknown>(
      api.delete<ApiResponse<unknown>>(`${BASE}/${sessionId}`, {
        params: { deleteImages },
      }),
    );
  },

  listAssets: (sessionId: string) =>
    callV3<UserAssetsListResponse>(
      api.get<ApiResponse<UserAssetsListResponse>>(`${BASE}/${sessionId}/assets`),
    ),

  getHistory: (sessionId: string, page = 1, pageSize = 50) =>
    callV3<HistoryListResponse>(
      api.get<ApiResponse<HistoryListResponse>>(`${BASE}/${sessionId}/history`, {
        params: { page, pageSize },
      }),
    ),

  undo: (sessionId: string) =>
    callV3<UndoRedoResponse>(
      api.post<ApiResponse<UndoRedoResponse>>(`${BASE}/${sessionId}/undo`),
    ),

  redo: (sessionId: string) =>
    callV3<UndoRedoResponse>(
      api.post<ApiResponse<UndoRedoResponse>>(`${BASE}/${sessionId}/redo`),
    ),

  revert: (sessionId: string, request: RevertToHistoryRequest) =>
    callV3<UndoRedoResponse>(
      api.post<ApiResponse<UndoRedoResponse>>(`${BASE}/${sessionId}/revert`, request),
    ),
};
